import { useAuth } from '@hb/react'
import { ELECTRONIC_CONSENT_AGREEMENTS, UserElectronicConsent } from '@hb/shared'
import { PropsWithChildren, useMemo } from 'react'
import { useDocument } from '../../../../../backend/db/hooks/useDocument'
import { confirmElectronicConsent } from './constants'
import { AgreementConfig, defaultPatientAgreementConfig, ElectronicConsentContext } from './context'
import { ElectronicConsentModal } from './ElectronicConsentModal'

export const ElectronicConsentProvider = ({
  patientId,
  children,
  modalOpen,
  setModalOpen,
}: PropsWithChildren<{
  patientId: string | null
  modalOpen: boolean
  setModalOpen: (open: boolean) => void
}>) => {
  const authUser = useAuth(s => s.authUser)
  const toBeSignedByMe = useMemo(() => {
    if (!patientId || !authUser) return false
    return patientId === authUser?.uid
  }, [patientId, authUser])
  const {
    data: consent,
    loading: consentLoading,
    error: consentLoadingError,
  } = useDocument<UserElectronicConsent>(ELECTRONIC_CONSENT_AGREEMENTS, patientId)

  const sortedSubmissions = useMemo(() => {
    if (!consent) return null
    return Object.values(consent?.submissions ?? {}).sort(
      (a, b) => new Date(b.signedOn).getTime() - new Date(a.signedOn).getTime(),
    )
  }, [consent])

  const mostRecentSubmission = useMemo(() => sortedSubmissions?.[0] ?? null, [sortedSubmissions])

  const config = useMemo<AgreementConfig>(
    () => ({
      ...defaultPatientAgreementConfig,
      confirm: () => confirmElectronicConsent({ patientId: patientId ?? '' }),
    }),
    [patientId],
  )

  return (
    <ElectronicConsentContext.Provider
      value={{
        signerId: patientId,
        toBeSignedByMe,
        consent,
        sortedSubmissions,
        mostRecentSubmission,
        consentLoading,
        consentLoadingError,
        config,
      }}>
      {children}
      <ElectronicConsentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </ElectronicConsentContext.Provider>
  )
}
